import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import {
  Edit3, RefreshCw, Loader2, Copy, Check, ExternalLink,
  Instagram, Facebook, Youtube, Twitter, Linkedin,
} from 'lucide-react';

// TikTok icon. Lucide doesn't ship one.
const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const PLATFORM_META = {
  instagram: { label: 'Instagram',      icon: Instagram, accent: 'text-pink-400' },
  facebook:  { label: 'Facebook',       icon: Facebook,  accent: 'text-blue-400' },
  tiktok:    { label: 'TikTok',         icon: TikTok,    accent: 'text-cyan-400' },
  youtube:   { label: 'YouTube Shorts', icon: Youtube,   accent: 'text-[#b3543f]' },
  x:         { label: 'X',              icon: Twitter,   accent: 'text-[#4a463b]' },
  linkedin:  { label: 'LinkedIn',       icon: Linkedin,  accent: 'text-sky-400' },
};

/**
 * Per-platform caption editor with platform-pill tabs + textarea.
 *
 * Shared by AutoPostItemDetailModal (Queue/History tabs) AND
 * PreviewPage (email-landing page). The component is pure presentation –
 * the parent owns the captions state + persistence. We just render
 * the tabs, the active platform's textarea, copy button, and an
 * optional "Regenerate" action.
 *
 * Props
 *   perPlatformCaptions   {platform: text}   . Current captions
 *   targetPlatforms       string[]           . Which platforms to show
 *   onChange(next)        ({platform: text}) . Called with the FULL
 *                                               next dict on every keystroke
 *   readOnly              bool               . Disables editing (post-fire state)
 *   onRegenerate          fn | null          . When provided, renders the
 *                                               "Neu generieren" button
 *   regenerating          bool               . Show spinner on regen button
 *   hideHeader            bool               . Caller renders its own header,
 *                                               skip ours
 */
export default function PerPlatformCaptionEditor({
  perPlatformCaptions = {},
  targetPlatforms = [],
  onChange,
  readOnly = false,
  onRegenerate,
  regenerating = false,
  hideHeader = false,
}) {
  const { t } = useTranslation();
  const [activePlatform, setActivePlatform] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Determine effective platform list: prefer the explicit targetPlatforms
  // (the queue item's `platforms` array), fall back to whatever keys
  // exist in perPlatformCaptions. Always filter to platforms we know
  // how to render an icon for, plus anything else (with a generic icon).
  const platformsToShow = (targetPlatforms.length
    ? targetPlatforms
    : Object.keys(perPlatformCaptions)
  ).filter(p => p && (perPlatformCaptions[p] != null || PLATFORM_META[p]));

  // Initialize / preserve active selection across rerenders.
  useEffect(() => {
    if (!platformsToShow.length) {
      setActivePlatform(null);
      return;
    }
    setActivePlatform((prev) => {
      if (prev && platformsToShow.includes(prev)) return prev;
      return platformsToShow[0];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformsToShow.join(',')]);

  const handleCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success(t('common.copied') || 'Kopiert');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (_) {
      toast.error(t('common.copyFailed') || 'Konnte nicht kopieren.');
    }
  };

  const updateActive = (text) => {
    if (!activePlatform || !onChange) return;
    onChange({ ...perPlatformCaptions, [activePlatform]: text });
  };

  if (!platformsToShow.length) return null;

  return (
    <div>
      {!hideHeader && (
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h3 className="text-sm font-medium text-[#23211a] flex items-center gap-1.5">
            <Edit3 className="w-3.5 h-3.5 text-[#1f4d3a]" />
            {readOnly
              ? (t('autoPost.detail.captionsReadOnly') || 'Captions')
              : (t('autoPost.detail.captionsEditable') || 'Captions (bearbeitbar pro Plattform)')}
          </h3>
          {!readOnly && onRegenerate && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRegenerate}
              disabled={regenerating}
              className="border-[#d8d0bd] text-[#23211a] hover:bg-[#efe9db] h-7 text-xs"
              title={t('autoPost.detail.regenerateHint') || 'KI generiert neue Captions'}
            >
              {regenerating ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  {t('autoPost.detail.regenerating') || 'Generiere…'}</>
              ) : (
                <><RefreshCw className="w-3 h-3 mr-1" />
                  {t('autoPost.detail.regenerate') || 'Neu generieren'}</>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Platform tab strip. Dim disabled platforms (no caption yet). */}
      <div className="flex flex-wrap gap-1 mb-2">
        {platformsToShow.map((p) => {
          const meta = PLATFORM_META[p] || { label: p, icon: ExternalLink, accent: 'text-[#6f6a5c]' };
          const Icon = meta.icon;
          const active = activePlatform === p;
          const hasContent = !!(perPlatformCaptions[p] || '').trim();
          return (
            <button
              key={p}
              type="button"
              onClick={() => setActivePlatform(p)}
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition ${
                active
                  ? 'bg-[#dfe8db] text-[#23211a] border-[#1f4d3a]/50'
                  : `bg-[#f2ecdf] ${meta.accent} border-[#d8d0bd] hover:border-[#1f4d3a]/50`
              } ${!hasContent ? 'opacity-50' : ''}`}
              data-testid={`platform-tab-${p}`}
            >
              <Icon className="w-3 h-3" />
              {meta.label}
            </button>
          );
        })}
      </div>

      {/* Active platform editor */}
      {activePlatform && (
        <div className="rounded-lg border border-[#e3dccb] bg-[#f7f3ea]/40">
          <div className="flex items-center justify-between px-3 py-1.5 bg-white border-b border-[#e3dccb]">
            <span className="text-[11px] text-[#8a8474]">
              {(perPlatformCaptions[activePlatform] || '').length}{' '}
              {t('autoPost.detail.chars') || 'Zeichen'}
            </span>
            <button
              type="button"
              onClick={() => handleCopy(activePlatform, perPlatformCaptions[activePlatform] || '')}
              className="text-[11px] text-[#6f6a5c] hover:text-[#23211a] inline-flex items-center gap-1"
            >
              {copiedKey === activePlatform
                ? <><Check className="w-3 h-3 text-[#1e7a4f]" /> {t('common.copied') || 'Kopiert'}</>
                : <><Copy className="w-3 h-3" /> {t('common.copy') || 'Kopieren'}</>}
            </button>
          </div>
          <textarea
            value={perPlatformCaptions[activePlatform] || ''}
            onChange={(e) => updateActive(e.target.value)}
            readOnly={readOnly}
            rows={8}
            className={`w-full px-3 py-2 text-sm text-[#23211a] bg-transparent border-0 focus:outline-none focus:ring-0 resize-y font-sans leading-relaxed ${
              readOnly ? 'cursor-default' : ''
            }`}
            data-testid={`per-platform-caption-${activePlatform}`}
          />
        </div>
      )}
    </div>
  );
}
